import styled, { css } from "styled-components";
import { ListBoxItem as ListBoxItemPrimitive } from "react-aria-components";
import { useDesignTokens, parseSizeToken } from "@boi/react-design-tokens";
import { TickIcon as BoiTickIcon } from "@boi/react-icons";

export const ListBoxItem = styled(ListBoxItemPrimitive)`
  ${() => {
    const { tokens } = useDesignTokens();
    const { select } = tokens;

    return css`
      display: grid;
      min-height: ${select.menu.option.minHeight};
      padding: ${select.menu.option.padding};
      background: ${select.menu.option.backgroundColor.base};
      border-bottom-width: ${select.menu.separator.width};
      border-bottom-style: ${select.menu.separator.style};
      border-bottom-color: ${select.menu.separator.color};
      cursor: pointer;
      outline: none;

      &[data-disabled] {
        cursor: not-allowed;
      }

      &:last-of-type {
        border-bottom-color: transparent;
      }
    `;
  }}
`;

export const ContentWrapper = styled.div`
  ${() => {
    const { tokens } = useDesignTokens();
    const { select } = tokens;

    return css`
      display: grid;
      align-items: center;
      color: ${select.menu.option.inner.color.base};
      padding-top: ${select.menu.option.inner.padding.top};
      padding-left: ${select.menu.option.inner.padding.left};
      padding-right: ${select.menu.option.inner.padding.right};
      padding-bottom: ${select.menu.option.inner.padding.bottom};
      border-radius: ${select.menu.option.inner.radius};
      background: ${select.menu.option.inner.backgroundColor.base};
      outline-width: ${select.menu.option.inner.stroke.width};
      outline-style: ${select.menu.option.inner.stroke.style};
      outline-color: ${select.menu.option.inner.stroke.color.base};
      outline-offset: ${select.menu.option.inner.stroke.offset};
      overflow: hidden;

      ${ListBoxItem}[data-selected] & {
        background: ${select.menu.option.inner.backgroundColor.selected};
        outline-color: ${select.menu.option.inner.stroke.color.selected};
      }

      ${ListBoxItem}[data-hovered] &,
      ${ListBoxItem}[data-focus-visible] & {
        background: ${select.menu.option.inner.backgroundColor.hover};
        outline-color: ${select.menu.option.inner.stroke.color.hover};
      }

      ${ListBoxItem}[data-pressed] & {
        background: ${select.menu.option.inner.backgroundColor.active};
        outline-color: ${select.menu.option.inner.stroke.color.active};
      }

      ${ListBoxItem}[data-disabled] & {
        color: ${select.menu.option.inner.color.disabled};
      }
    `;
  }}
`;

type ContentProps = {
  $withIcon: boolean;
};

export const Content = styled.div<ContentProps>`
  ${({ $withIcon }) => {
    const { tokens } = useDesignTokens();
    const { select } = tokens;

    return [
      css`
        display: grid;
        grid-template-columns: auto 1fr;
        gap: ${select.menu.option.inner.gap};
        overflow-wrap: anywhere;
      `,
      $withIcon &&
        css`
          grid-template-columns: auto auto 1fr;
        `,
    ];
  }}
`;

export const TickIconWrapper = styled.div`
  ${() => {
    const { tokens } = useDesignTokens();
    const { select } = tokens;

    return [
      css`
        display: grid;
        place-items: center;
        width: ${select.menu.option.inner.tickIconWrapper.width};
        height: ${parseSizeToken(
          select.menu.option.inner.tickIconWrapper.height
        )};
      `,
    ];
  }}
`;

export const TickIcon = styled(BoiTickIcon)`
  ${() => {
    const { tokens } = useDesignTokens();
    const { select } = tokens;

    return [
      css`
        width: ${select.menu.option.inner.tickIcon.width};
        height: ${select.menu.option.inner.tickIcon.height};
        color: ${select.menu.option.inner.tickIcon.color};
      `,
    ];
  }}
`;

export const IconWrapper = styled.div`
  ${() => {
    const { tokens } = useDesignTokens();
    const { select } = tokens;

    return [
      css`
        display: grid;
        place-items: center;
        margin-right: 4px;
        height: ${parseSizeToken(select.menu.option.inner.iconWrapper.height)};

        svg {
          width: ${select.menu.option.inner.icon.width};
          max-height: ${select.menu.option.inner.icon.maxHeight};
          color: ${select.menu.option.inner.icon.color};
          opacity  ${select.menu.option.inner.icon.opacity.base};

          ${ListBoxItem}[data-disabled] & {
            opacity: ${select.menu.option.inner.icon.opacity.disabled};
          }
        }
      `,
    ];
  }}
`;
